/**
 * Client provider organism wrapping app with Clerk auth, ThemeProvider, and PostHog analytics.
 * Captures pageviews on route changes and identifies users post sign-in/sign-up.
 */
"use client";

import { ClerkProvider } from "@clerk/nextjs";
import { Suspense, useEffect, useRef } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import * as Sentry from "@sentry/nextjs";
import { useUser } from "@clerk/nextjs";

import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { trackUserFirstSessionStarted } from "@/lib/analytics/client";
import { SS_SESSION_EXPIRED, SS_LAST_AUTH_FLOW, SS_FIRST_SESSION_SENT } from "@/lib/storage-keys";
import {
  SS_REFERRAL_ID,
  DEFAULT_REFERRAL_ID,
  readReferralCookie,
  resolveReferralIdForCapture,
  writeReferralCookie,
  normalizeMaybeReferralId,
} from "@/lib/referral";
import {
  clerkPublishableKey,
  clerkSignInUrl,
  clerkSignUpUrl,
} from "@/lib/config";
import { FeedbackButton } from "@/components/molecules/FeedbackButton";
import { isCanvasRoute, isLoggedInShellRoute } from "@/lib/routes";

/**
 * Captures a PostHog `$pageview` on every route change.
 *
 * A leaf component rather than an effect inside `ClientProvider`, purely
 * because of `useSearchParams()`: during prerender it bails the nearest
 * Suspense boundary to client-side rendering, and any boundary containing
 * `{children}` takes the entire page down with it. Isolated here, the bailout
 * costs nothing — this component renders `null`.
 */
function PageviewCapture() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
    if (!pathname) return;

    let url = window.location.origin + pathname;
    const qs = searchParams?.toString();
    if (qs) url += `?${qs}`;
    // Capture pageview on route changes when PostHog is initialized
    posthog.capture("$pageview", { $current_url: url });
  }, [pathname, searchParams]);

  return null;
}

/**
 * Captures the best available referral from cookie, legacy sessionStorage,
 * path, or ?src=. Also a leaf for the `useSearchParams()` reason above.
 */
function ReferralCapture() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const cookieReferralId = readReferralCookie();
    let legacySessionReferralId: string | null = null;

    if (!cookieReferralId) {
      try {
        legacySessionReferralId = window.sessionStorage.getItem(SS_REFERRAL_ID);
      } catch {
        /* benign: sessionStorage unavailable (private mode, disabled) */
      }
    }

    const referralId = resolveReferralIdForCapture({
      storedReferralId: cookieReferralId ?? legacySessionReferralId,
      pathname,
      src: searchParams.get("src"),
      referralId: searchParams.get("referral_id"),
    });

    if (referralId && referralId !== cookieReferralId) {
      writeReferralCookie(referralId);
    }
  }, [pathname, searchParams]);

  return null;
}

/**
 * Accounts within this window on either side of Date.now() are treated as just-signed-up.
 * The window is symmetric: browser and Clerk clocks differ by seconds, so a small negative
 * age (browser behind Clerk) is normal at first identify. An age beyond the window in either
 * direction is not a new account.
 */
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;

function ClerkIdentify() {
  const { user, isSignedIn } = useUser();
  const hasSentAuthEventRef = useRef(false);

  // Derive primitives from user fields so the effect dependency is stable
  // (unsafeMetadata and createdAt are new object references each render; strings/numbers are not).
  const rawMetaReferral = user?.unsafeMetadata?.referral_id;
  const metaReferralStr = typeof rawMetaReferral === "string" ? rawMetaReferral : null;
  const createdAtMs = user?.createdAt instanceof Date ? user.createdAt.getTime() : null;

  // Browser Sentry events carry the Clerk id so affected-user counts and the
  // "issue affecting N users" alerts see client errors (LEV-2330). Same id as
  // captureServerError sends; id only, never email or name. Not gated on
  // PostHog: error attribution must not depend on analytics being configured.
  useEffect(() => {
    Sentry.setUser(isSignedIn && user?.id ? { id: user.id } : null);
  }, [isSignedIn, user?.id]);

  // Identify user when signed in (runs only when inside ClerkProvider)
  useEffect(() => {
    if (!process.env.NEXT_PUBLIC_POSTHOG_KEY) return;
    if (isSignedIn && user?.id) {
      try {
        const previousId = posthog.get_distinct_id();
        // referral_id goes in $set_once (3rd arg), never $set: a returning user on a new
        // browser must not overwrite the referral recorded at signup. Source is Clerk
        // unsafeMetadata ONLY — the live cookie reflects the current visit, not the signup.
        // The default applies ONLY to a just-created account: the server writes the same
        // default for it seconds later, so this keeps the Slack destination in sync.
        // An older account with no referral predates referral tracking and must not be stamped.
        // The age window is symmetric (Math.abs): browser and Clerk clocks differ by seconds,
        // so a small negative age (browser behind) is normal at first identify.
        // fixed on 2026-09-17: LEV-1772, LEV-1774
        const isNewAccount = createdAtMs !== null && Math.abs(Date.now() - createdAtMs) < NEW_ACCOUNT_WINDOW_MS;
        const referralId = normalizeMaybeReferralId(metaReferralStr) ?? (isNewAccount ? DEFAULT_REFERRAL_ID : null);
        const setOnce = referralId ? { referral_id: referralId } : undefined;
        posthog.identify(
          user.id,
          { email: user.primaryEmailAddress?.emailAddress, name: user.fullName },
          setOnce,
        );
        if (previousId && previousId.includes("@") && previousId !== user.id) {
          posthog.capture("$merge_dangerously", { alias: previousId });
        }
        // Order is load-bearing: identify (above) is synchronous and queued
        // first, so the PostHog Slack destination reads the person properties
        // it carries (referral_id, onboarding_*) when this event is ingested.
        // The sessionStorage key stops re-sends on navigation within the
        // 10-minute window in this tab. When storage is blocked there is no
        // guard, so the event repeats once per effect run; PostHog's 30-min
        // per-person mask on the destination is what folds those into one
        // Slack message.
        // fixed on 2026-09-18: LEV-1786
        if (isNewAccount) {
          let alreadySent = false;
          try {
            alreadySent = window.sessionStorage.getItem(SS_FIRST_SESSION_SENT) === user.id;
          } catch {
            /* benign: sessionStorage unavailable (private mode); event fires once per page load */
          }
          if (!alreadySent) {
            trackUserFirstSessionStarted();
            try {
              window.sessionStorage.setItem(SS_FIRST_SESSION_SENT, user.id);
            } catch {
              /* benign: sessionStorage unavailable (private mode) */
            }
          }
        }
      } catch {
        /* benign: PostHog identify failure is non-critical */
      }

      try {
        window.sessionStorage.removeItem(SS_SESSION_EXPIRED);
      } catch {
        /* benign: sessionStorage unavailable (private mode, disabled) */
      }

      try {
        const flow = window.sessionStorage.getItem(SS_LAST_AUTH_FLOW);
        if (flow && !hasSentAuthEventRef.current) {
          hasSentAuthEventRef.current = true;
          if (flow === "sign-in") {
            posthog.capture(ANALYTICS_EVENTS.USER_SIGNED_IN);
          }
          window.sessionStorage.removeItem(SS_LAST_AUTH_FLOW);
        }
      } catch {
        /* benign: sessionStorage unavailable (private mode, disabled) */
      }
    }
  }, [isSignedIn, user?.id, user?.fullName, user?.primaryEmailAddress?.emailAddress, metaReferralStr, createdAtMs]);

  return null;
}

export default function ClientProvider({ children }: { children: React.ReactNode }) {
  const isClerkDisabled =
    !clerkPublishableKey || !clerkPublishableKey.startsWith("pk_");

  // `usePathname()` is safe to call here — unlike `useSearchParams()` it does
  // not trigger a prerender bailout, so it cannot blank the page.
  const pathname = usePathname();

  // On routes that own their own sidebar (canvas + logged-in shell): hide the
  // Next.js dev indicator via CSS class — the sidebar wrench toggles it back.
  const onCanvas = isCanvasRoute(pathname);
  const onLoggedInShell = isLoggedInShellRoute(pathname);
  const inSidebarShell = onCanvas || onLoggedInShell;
  useEffect(() => {
    if (process.env.NODE_ENV !== "development") return;
    document.body.classList.toggle("hide-dev-indicator", inSidebarShell);
    return () => document.body.classList.remove("hide-dev-indicator");
  }, [inSidebarShell]);

  // Hide floating feedback when a sidebar is present — both the canvas and
  // logged-in shell sidebars carry a "Give feedback" item in their help menu.
  const showFeedbackButton = !inSidebarShell;

  if (isClerkDisabled) {
    return (
      <>
        <Suspense fallback={null}>
          <PageviewCapture />
        </Suspense>
        {children}
        {showFeedbackButton && <FeedbackButton />}
      </>
    );
  }

  return (
    <ClerkProvider
      publishableKey={clerkPublishableKey}
      appearance={{
        options: {
          logoPlacement: "inside",
          // Clerk v7 hides optional sign-up fields (such as name) by default;
          // v6 showed them, and founders still expect to enter their name.
          showOptionalFields: true,
        },
      }}
      signInUrl={clerkSignInUrl}
      signUpUrl={clerkSignUpUrl}
      localization={{
        locale: "en-US",
      }}
    >
      <Suspense fallback={null}>
        <PageviewCapture />
        <ReferralCapture />
      </Suspense>
      <ClerkIdentify />
      {children}
      {showFeedbackButton && <FeedbackButton />}
    </ClerkProvider>
  );
}
